import { Dialog, DialogBody, DialogContent, DialogTrigger } from '@civilgrid/ui/components';
import { SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { type ReactNode, useEffect, useMemo } from 'react';
import { useAsyncFn } from 'react-use';
import { getZoningDetailsAtPoint } from '~/api/misc2';
import type { ZoningFeature } from './store';

interface Props {
  zone: ZoningFeature;
  trigger: ReactNode;
}

export default function ZoningDetails({ zone, trigger }: Props) {
  const [data, loadZoningDetails] = useAsyncFn(async () => {
    const z = await getZoningDetailsAtPoint(zone.point.lng, zone.point.lat);
    return z;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    loadZoningDetails();
  }, [zone]);

  const dialogContent = useMemo(() => {
    if (data.loading) {
      return <SkeletonSpinnerCentered />;
    }
    if (data.value) {
      return (
        <div className="max-h-[80vh] overflow-y-auto rounded-lg bg-white p-4">
          {data.value ? (
            <div>
              <h3 className="mt-4 text-base font-semibold">Zoning Controls</h3>
              <table className="w-full table-auto text-left text-xs">
                <thead>
                  <tr>
                    <th className="border-b py-1 pr-2">Control Type</th>
                    <th className="border-b py-1 pr-2">Standard</th>
                    <th className="border-b py-1 pr-2">Non-Standard</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-b py-1 pr-2">Max FAR</td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.far_standard?.standard?.max_far || 'NA'}
                    </td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.far_standard?.['non-standard']?.maximum_floor_area_ratio || 'NA'}
                    </td>
                  </tr>
                  <tr>
                    <td className="border-b py-1 pr-2">Min Lot Area (sq ft)</td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.lot_standard?.standard?.min_lot_area_sq_ft || 'NA'}
                    </td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.lot_standard?.['non-standard']?.minimum_lot_area_sq_feet || 'NA'}
                    </td>
                  </tr>
                  <tr>
                    <td className="border-b py-1 pr-2">Max Height (ft)</td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.building_height_standard?.standard?.max_building_height_ft || 'NA'}
                    </td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.building_height_standard?.['non-standard']?.maximum_building_height_feet ||
                        'NA'}
                    </td>
                  </tr>
                  <tr>
                    <td className="border-b py-1 pr-2">Setback (ft)</td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.side_setback_standard?.standard?.min_side_yard_ft || 'NA'}
                    </td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.side_setback_standard?.['non-standard']?.minimum_side_yard_feet || 'NA'}
                    </td>
                  </tr>
                  <tr>
                    <td className="border-b py-1 pr-2">Max Density (du/acre)</td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.density_standard?.standard?.max_density_du_per_acre || 'NA'}
                    </td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.density_standard?.['non-standard']?.maximum_density_du_per_acre || 'NA'}
                    </td>
                  </tr>
                  <tr>
                    <td className="border-b py-1 pr-2">Max Coverage</td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.coverage_standard?.standard?.max_coverage || 'NA'}
                    </td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.coverage_standard?.['non-standard']?.maximum_coverage_percentage || 'NA'}
                    </td>
                  </tr>
                  <tr>
                    <td className="border-b py-1 pr-2">Open Space Percentage</td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.pervious_standard?.standard?.open_space_percentage || 'NA'}
                    </td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.pervious_standard?.['non-standard']
                        ?.minimum_usable_open_space_percentage || 'NA'}
                    </td>
                  </tr>
                  <tr>
                    <td className="border-b py-1 pr-2">Min Lot Width (ft)</td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.lot_width_standard?.standard?.min_lot_width_ft || 'NA'}
                    </td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.lot_width_standard?.['non-standard']?.mean_lot_width_feet || 'NA'}
                    </td>
                  </tr>
                  <tr>
                    <td className="border-b py-1 pr-2">Min Rear Yard (ft)</td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.rear_setback_standard?.standard?.min_rear_yard_ft || 'NA'}
                    </td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.rear_setback_standard?.['non-standard']?.minimum_rear_yard_feet || 'NA'}
                    </td>
                  </tr>
                  <tr>
                    <td className="border-b py-1 pr-2">Min Front Yard (ft)</td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.front_setback_standard?.standard?.min_front_yard_ft || 'NA'}
                    </td>
                    <td className="border-b py-1 pr-2">
                      {data.value?.controls?.front_setback_standard?.['non-standard']?.minimum_front_yard_feet || 'NA'}
                    </td>
                  </tr>
                </tbody>
              </table>
              <h3 className="mt-4 font-semibold">Land Uses</h3>
              <p>
                <strong>As of Right:</strong> {data.value?.permitted_land_uses?.as_of_right?.join(', ')}
              </p>
              <p>
                <strong>Conditional Uses:</strong> {data.value?.permitted_land_uses?.conditional_uses?.join(', ')}
              </p>
              <p>
                <strong>Prohibited Uses:</strong> {data.value?.permitted_land_uses?.prohibited?.join(', ')}
              </p>
            </div>
          ) : (
            <p>No metadata available for this feature.</p>
          )}
        </div>
      );
    }
    return <div>⚠️ Oops, please try again. If the issue persists, please contact support.</div>;
  }, [data]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="block rounded-sm text-left focus-ring"
          data-testid={`zoning-details-trigger-${zone.zone_code}`}
        >
          {trigger}
        </button>
      </DialogTrigger>
      <DialogContent
        title={`${zone.zone_name} (${zone.zone_code})`}
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid="zoning-details-dialog"
      >
        <DialogBody>{dialogContent}</DialogBody>
      </DialogContent>
    </Dialog>
  );
}
